import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepButton from "@mui/material/StepButton";
import SFTypography from "../typography";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import SFContainer from "../container";
import StepperActionBtns from "./stepperActionBtns";
import ConfirmDialog from "./confirmDialog";
import getStyled from "../../hooks/getStyled";
import { SFCheckIcon } from "../icons";
import { stepperActions } from "../../redux/reducers/stepperReducer";
import StepActions from "../../constants/stepActions";
import SFStep from "./step";
import { SFGridContainerItem, SFGridItem, SFStack } from "../grid";
import { Container } from "@mui/material";
import CreateReactPortal from "../../hooks/createReactPortal";
import VerticalStepperComponent from "./verticalStepperComponent";
import SFSideDrawer from "../drawer/sideDrawer";
//#region Styled

const { styled } = getStyled();

const StyledStepperWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(2.5, 0),
  backgroundColor: theme.palette.white.main,
  height: "100%",
  flex: 1,
}));

const ColorlibConnector = styled(StepConnector)(({ theme, orientation }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 15,
  },
  [`& .${stepConnectorClasses.line}`]: {
    ...(orientation === "vertical"
      ? { borderLeft: "2px solid" }
      : { borderTop: "2px solid" }),
    borderColor: theme.palette.grey200.main,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.green400.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.green400.main,
    },
  },
}));

const ColorlibStepIconRoot = styled("div")(({ theme, ownerState }) => ({
  ...theme.typography.h6,
  color: theme.palette.grey400.main,
  backgroundColor: theme.palette.grey100.main,
  zIndex: 1,
  width: 25,
  height: 25,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  ...(ownerState.active && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.white.main,
  }),
  ...(ownerState.completed && {
    backgroundColor: theme.palette.green300.main,
    color: theme.palette.white.main,
    "& .MuiSvgIcon-root": {
      fontSize: 24,
    },
  }),
}));

const StyledContainer = styled(SFContainer)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  //calculated height based on stepper actionButtons bar
  height: "100%",
}));

const ColorlibStepIcon = (props) => {
  const { active, completed, className, icon } = props;

  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}
    >
      {completed ? <SFCheckIcon /> : icon}
    </ColorlibStepIconRoot>
  );
};

ColorlibStepIcon.propTypes = {
  /**
   * Whether this step is active.
   * @default false
   */
  active: PropTypes.bool,
  className: PropTypes.string,
  /**
   * Mark the step as completed. Is passed to child components.
   * @default false
   */
  completed: PropTypes.bool,
  /**
   * The label displayed in the step icon.
   */
  icon: PropTypes.node,
};

const StyledStepperContent = styled("div", {
  shouldForwardProps: (prop) =>
    prop !== "disableActionBtns" &&
    prop !== "isVertical" &&
    prop !== "disableGutters",
})(({ theme, disableActionBtns, isVertical, disableGutters = false }) => ({
  ...(isVertical && { flex: 1 }),
  padding: disableGutters ? 0 : theme.spacing(2, 0),
  backgroundColor: theme.palette.background.default,
  minHeight: 400,
  height: "100%",
  //add this padding in order to be visible all the content and not hide by the actions buttons bar
  paddingBottom: !disableActionBtns ? (!disableGutters ? 80 : 56) : 0,
}));

const StepContent = ({
  disableGutters,
  disableActionBtns,
  orientation,
  steps,
  name,
  loading,
}) => (
  <StyledStepperContent
    disableActionBtns={disableActionBtns}
    isVertical={orientation === "vertical"}
    disableGutters={disableGutters}
  >
    <StyledContainer disableGutters={disableGutters}>
      {steps.map((child, index) => {
        return (
          <SFStep key={index} step={index} stepperName={name} loading={loading}>
            {child.props.children}
          </SFStep>
        );
      })}
    </StyledContainer>
  </StyledStepperContent>
);

//#endregion

const SFStepper = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const steps = props.children.filter((x) => x);

  const stepsArray = steps.map((child) => ({
    label: child.props.label,
    subLabel: child.props.subLabel,
  }));

  const stepperReduxState = useSelector((state) => {
    return state.stepper.find((stepper) => stepper.name === props.name);
  });

  useEffect(() => {
    setInitialData();
  }, []);

  const setInitialData = () => {
    dispatch(
      props.stepperData
        ? stepperActions.setInitialData({
          steps: props.stepperData.data,
          activeStep: props.stepperData.activeStep,
          name: props.name,
        })
        : stepperActions.setInitialData({
          steps: stepsArray.map((step) => {
            // If the label is an object, it means that it has a translationKey property
            // (see VerticalStepperComponent.propTypes), so we need to translate the key
            // and set the translation as the label.
            let { label, subLabel, ...rest } = step; // subLabel will not be stored in Redux
            if (typeof label === "object") {
              label = t(label.translationKey);
            }
            const stepToStore = {
              ...rest,
              label,
              data: {},
            };
            return stepToStore;
          }),
          activeStep: props.defaultActiveStep,
          name: props.name,
        })
    );
    //this is used to inform the steps that the data has been set and they can render
    setLoading(false);
  };

  const resetStepperData = () => {
    dispatch(
      stepperActions.resetData({
        steps: stepsArray,
        name: props.name,
      })
    );
  };

  const isStepOptional = (stepIndex) => {
    return stepsArray[stepIndex].optional;
  };

  const clickStepHandler = (step) => {
    if (!props.disableClickNavigation) {
      dispatch(
        stepperActions.triggerStepChange({
          stepAction: StepActions.SPECIFIC,
          step,
          name: props.name,
        })
      );
    }
  };

  const closeDialogHandler = async (confirm = false) => {
    if (confirm && stepperReduxState.dialog.finish) {
      !props.keepFinishDialogOpen &&
        dispatch(stepperActions.closeDialog({ name: props.name }));
      const finishHandler = async () => {
        const res = await props.onFinish(stepperReduxState.data);
        if (res) {
          resetStepperData();
        }
      };
      await finishHandler();
      props.keepFinishDialogOpen &&
        dispatch(stepperActions.closeDialog({ name: props.name }));
    } else if (confirm) {
      props.onCancel();
      resetStepperData();
    } else {
      dispatch(stepperActions.closeDialog({ name: props.name }));
    }
  };

  const StepperComponent =
    props.orientation === "vertical" ? (
      <VerticalStepperComponent
        activeStep={stepperReduxState?.activeStep}
        clickStepHandler={clickStepHandler}
        stepIconComponent={ColorlibStepIcon}
        stepsArray={stepsArray}
        disableClickNavigation={props.disableClickNavigation}
      />
    ) : (
      <StyledStepperWrapper>
        <Container sx={(theme) => ({ maxWidth: theme.screenMaxWidth })}>
          <SFGridContainerItem justifyContent="space-around">
            <SFGridItem xs={7}>
              <Stepper
                sx={{ mx: 2 }}
                activeStep={stepperReduxState?.activeStep}
                alternativeLabel
                connector={<ColorlibConnector />}
              >
                {stepsArray.map((step, index) => {
                  const labelProps = {};
                  if (isStepOptional(index)) {
                    labelProps.optional = (
                      <SFTypography variant="body1">
                        {t("components.stepper.optionalLabel")}
                      </SFTypography>
                    );
                  }
                  return (
                    <Step key={step.label}>
                      <StepButton
                        color="inherit"
                        onClick={clickStepHandler.bind(this, index)}
                      >
                        <StepLabel
                          StepIconComponent={ColorlibStepIcon}
                          {...labelProps}
                        >
                          {step.label}
                        </StepLabel>
                      </StepButton>
                    </Step>
                  );
                })}
              </Stepper>
            </SFGridItem>
          </SFGridContainerItem>
        </Container>
      </StyledStepperWrapper>
    );

  return (
    <>
      <ConfirmDialog
        stepperName={props.name}
        onClose={closeDialogHandler}
        title={
          props.dialogTitle ??
          t("components.stepper.default.confirmDialogHeader")
        }
        confirmationMessages={{
          submitBtnLabel: props.confirmationMessages?.submitBtnLabel,
          submit:
            props.confirmationMessages?.submit ??
            t("components.stepper.default.messages.submitWizardConfirmation"),
          cancel:
            props.confirmationMessages?.cancel ??
            t("components.stepper.default.messages.cancelWizardConfirmation"),
        }}
      />
      {props.orientation === "vertical" ? (
        <SFStack direction="row" flex={1}>
          <SFSideDrawer disablePortal>{StepperComponent}</SFSideDrawer>
          <StepContent
            disableGutters={props.disableGutters}
            disableActionBtns={props.disableActionBtns}
            orientation={props.orientation}
            steps={steps}
            name={props.name}
            loading={loading}
          />
        </SFStack>
      ) : (
        <>
          {CreateReactPortal(
            StepperComponent,
            document.getElementById("stepperRoot"),
            props.disablePortal
          )}
          <StepContent
            disableGutters={props.disableGutters}
            disableActionBtns={props.disableActionBtns}
            orientation={props.orientation}
            steps={steps}
            name={props.name}
            loading={loading}
          />
          {!props.disableActionBtns && (
            <StepperActionBtns
              stepsLength={stepsArray.length}
              stepperName={props.name}
              placement={props.actionsPlacement}
              actions={props.actions}
            />
          )}
        </>
      )}
    </>
  );
};

SFStepper.defaultProps = {
  defaultActiveStep: 0,
  keepFinishDialogOpen: true,
};

SFStepper.propTypes = {
  defaultActiveStep: PropTypes.number,
  dialogTitle: PropTypes.node,
  confirmationMessages: PropTypes.shape({
    submit: PropTypes.object,
    cancel: PropTypes.string,
    submitBtnLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }),
  children: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  onFinish: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      function: PropTypes.func,
      icon: PropTypes.node,
    })
  ),
  actionsPlacement: PropTypes.oneOf([
    "bottom-end",
    "bottom-start",
    "bottom",
    "left-end",
    "left-start",
    "left",
    "right-end",
    "right-start",
    "right",
    "top-end",
    "top-start",
    "top",
    "bottom",
  ]),
  stepperData: PropTypes.shape({
    nextStep: PropTypes.number,
    activeStep: PropTypes.number,
    data: PropTypes.arrayOf(
      PropTypes.shape({
        step: PropTypes.number,
        data: PropTypes.any,
      })
    ),
  }),
  disableActionBtns: PropTypes.bool,
  disableClickNavigation: PropTypes.bool,
  disableGutters: PropTypes.bool,
  /**
   * When `true`, the finish confirmation dialog will only be closed when the async `onFinish` function is resolved.
   * When `false`, the finish confirmation dialog will be closed immediately after the `onFinish` function is called.
   */
  keepFinishDialogOpen: PropTypes.bool,
};

export default SFStepper;
